import {
  AxiosError,
  AxiosResponse,
  CanceledError,
} from 'axios';
import {
  computed,
  ref, shallowRef,
} from 'vue';

import {
  globalConcurrencyManager,
} from '@/utils/concurrency.ts';

type TCallback<RESPONSE_DATA = any, CALLBACK_ARGS = any> = (abortController: AbortController, data?: CALLBACK_ARGS) => Promise<AxiosResponse<RESPONSE_DATA>>;
type TData<RESPONSE_DATA> = Awaited<ReturnType<TCallback<RESPONSE_DATA>>>;

interface IConcurrencyOptions {
  /*
   * Used to scope the concurrency limitation.
   */
  key: string,
  /*
   * Defines how many request can be done in parallel. Default 5.
   */
  limit?: number,
}

interface IOptions<RESPONSE_DATA = any> {
  /**
   * True: It will refetch if execute() is called. It aborts ongoing requests
   * False: Caches data does not refetch
   */
  shouldBeAborted?: boolean,
  /**
   * If shouldBeAborted === true and throwCancelError === true then a CancelError
   * is thrown in case the request is aborted.
   */
  throwCancelError?: boolean,
  /**
   * If errors should be thrown
   * If error are not thrown its expected to be handled by consumer
   *
   * @default true
   */
  throwOnError?: boolean,
  onSuccess?: (data: AxiosResponse<RESPONSE_DATA>) => void,
  onError?: (data: AxiosError<RESPONSE_DATA> | Error) => void,
  /**
   * Works as vue-watcher. If set to true, the data is fetched immediately. Otherwise,
   * data needs to be fetched by calling execute().
   */
  immediate?: boolean,
  /**
   * If value should be reset while waiting for new value
   */
  resetOnLoad?: boolean,
  concurrency?: IConcurrencyOptions,
}

export function useAxiosRequest<RESPONSE_DATA = any, CALLBACK_ARGS = any>(callback: TCallback<RESPONSE_DATA, CALLBACK_ARGS>, options: IOptions<RESPONSE_DATA> = {}) {
  // Use a request counter to prevent aborted requests from prematurely setting isLoading to false
  const loadsCount = ref<number>(0);
  const abortController = shallowRef(new AbortController());
  const data = shallowRef<TData<RESPONSE_DATA> | null>(null);
  const error = shallowRef<AxiosError<RESPONSE_DATA> | Error | null>(null);
  const promise = shallowRef<Promise<AxiosResponse<RESPONSE_DATA>> | null>(null);
  const isLoading = computed<boolean>(() => loadsCount.value > 0);
  /**
  * True only while reloading on top of already-present data (background refetch),
  * as opposed to the initial load where there is no data yet.
  */
  const isRefetching = computed<boolean>(() => isLoading.value && data.value !== null);
  const throwOnError = options.throwOnError === undefined || options.throwOnError;
  // used in case error was silenced, so we would not call onSuccess
  let hadError = false;

  if (options.concurrency && options.concurrency?.limit !== undefined) {
    globalConcurrencyManager.setLimit(options.concurrency.key, options.concurrency.limit);
  }

  async function execute(args?: CALLBACK_ARGS) {
    if (promise.value && !options.shouldBeAborted) {
      return promise.value;
    }
    hadError = false;
    loadsCount.value++;
    if (options.concurrency) {
      await globalConcurrencyManager.acquire(options.concurrency.key);
    }
    if (options.shouldBeAborted) {
      abortController.value.abort();
      abortController.value = new AbortController();
      error.value = null;
    }
    if (options.resetOnLoad) {
      data.value = null;
    }
    promise.value = callback(abortController.value, args);
    try {
      data.value = await promise.value!;
    } catch (_error) {
      hadError = true;
      if (!(_error instanceof CanceledError)
        || options.throwCancelError) {
        error.value = _error as AxiosError<RESPONSE_DATA> | Error;

        if (options.onError) {
          options.onError(_error as AxiosError<RESPONSE_DATA> | Error);
        }

        if (throwOnError) {
          throw _error;
        }
      }
    } finally {
      if (options.concurrency) {
        globalConcurrencyManager.release(options.concurrency.key);
      }
      loadsCount.value--;
    }
    if (options.onSuccess && !hadError) {
      options.onSuccess(data.value!);
    }
    return data.value!;
  }

  function reset() {
    abortController.value.abort();
    abortController.value = new AbortController();
    data.value = null;
    error.value = null;
    promise.value = null;
    loadsCount.value = 0;
  }

  if (options.immediate) {
    execute();
  }

  return {
    isLoading,
    isRefetching,
    abortController,
    execute,
    data,
    promise,
    error,
    reset,
  };
}
